import { createAction, handleActions } from 'redux-actions';
import { pender } from 'redux-pender';
import produce from 'immer';
import * as api from 'app/api';
import carVerificationType from 'app/constants/carVerificationType';
import { loggingException } from 'app/libs/sentry';
import set from 'lodash/set';

const onError = (state, action) =>
	produce(state, () => {
		const error = action.payload;

		if (!error.response?.data) {
			return;
		}

		const { status } = error.response;
		const { message, messages } = error.response.data;
		const msg = message || messages?.[0] || Error('알 수 없는 에러가 발생했습니다. 다시 시도해주세요.');

		if (status >= 500 && (message || msg instanceof Error)) loggingException(error);
		alert(msg);
	});

const valueMaps = (values, pictures) => {
	const valuesMap = new Map();
	const faultOptionMap = new Map();
	const faultStatusMap = new Map();
	values.forEach((item) => {
		valuesMap.set(item.option.id, item.value);
		faultOptionMap.set(item.option.id, item.faultOptionValue);
		faultStatusMap.set(item.option.id, item.faultStatusValue);
	});
	pictures.forEach((item) => {
		valuesMap.set(item.optionId, item.attachments);
	});
	return { valuesMap, faultOptionMap, faultStatusMap };
};

const GET_CAR_VERIFICATION_OPTION = 'carVerification/GET_CAR_VERIFICATION_OPTION';
export const getCarVerificationOptionAction = createAction(GET_CAR_VERIFICATION_OPTION, api.getCarVerificationOption, (category) => ({ category }));

const GET_CAR_VERIFICATION_INFO = 'carVerification/GET_CAR_VERIFICATION_INFO';
export const getCarVerificationInfoAction = createAction(GET_CAR_VERIFICATION_INFO, api.getCarVerificationInfo, (id, category) => ({ id, category }));

const GET_DIAGNOSIS_INFO = 'carVerification/GET_DIAGNOSIS_OPTION_INFO';
export const getDiagnosisInfoAction = createAction(
	GET_DIAGNOSIS_INFO,
	(id) => Promise.all([api.getCarVerificationInfo(id, 'diagnosis'), api.getCarVerificationOption('DIAGNOSIS')]),
	(id) => ({ id, category: 'diagnosis' }),
);

const GET_DIAGNOSIS_USER_INFO = 'carVerification/GET_DIAGNOSIS_USER_INFO';
export const getDiagnosisUserInfoAction = createAction(GET_DIAGNOSIS_USER_INFO, api.getDiagnosisUserInfo);

const GET_DIAGNOSIS_REG_STATUS = 'carVerification/GET_DIAGNOSIS_REG_STATUS';
export const getDiagnosisRegStatusAction = createAction(GET_DIAGNOSIS_REG_STATUS, api.getDiagnosisRegStatus);

const GET_CAR_VERIFICATION_COMPLETED = 'carVerification/GET_CAR_VERIFICATION_COMPLETED';
export const getCarVerificationCompletedAction = createAction(GET_CAR_VERIFICATION_COMPLETED, api.getCarVerificationCompleted);

const GET_CAR_VERIFICATION_CONDITIONS = 'carVerification/GET_CAR_VERIFICATION_CONDITIONS';
export const getCarVerificationConditionsAction = createAction(GET_CAR_VERIFICATION_CONDITIONS, api.getCarVerificationConditions);

const SET_CAR_VERIFICATION_USER_TYPE = 'carVerification/SET_CAR_VERIFICATION_USER_TYPE';
export const setCarVerificationUserTypeAction = createAction(SET_CAR_VERIFICATION_USER_TYPE);

const SAVE_CAR_VERIFICATION_VALUES_IN_STORE = 'carVerification/SAVE_CAR_VERIFICATION_VALUES_IN_STORE';
export const saveCarVerificationValuesInStoreAction = createAction(SAVE_CAR_VERIFICATION_VALUES_IN_STORE);

const SAVE_CAR_VERIFICATION_INFO = 'carVerification/SAVE_CAR_VERIFICATION_INFO';
export const saveCarVerificationInfoAction = createAction(SAVE_CAR_VERIFICATION_INFO, api.saveCarVerificationInfo);

const ENROLL_CAR_VERIFICATION_INFO = 'carVerification/ENROLL_CAR_VERIFICATION_INFO';
export const enrollCarVerificationInfoAction = createAction(ENROLL_CAR_VERIFICATION_INFO, api.enrollCarVerificationInfo);

const UPDATE_CAR_VERIFICATION_INFO = 'carVerification/UPDATE_CAR_VERIFICATION_INFO';
export const updateCarVerificationInfoAction = createAction(UPDATE_CAR_VERIFICATION_INFO, api.updateCarVerificationInfo);

const CLEAR_CAR_VERIFICATION_VALUES = 'carVerification/CLEAR_CAR_VERIFICATION_VALUES';
export const clearCarVerificationValuesAction = createAction(CLEAR_CAR_VERIFICATION_VALUES);

const GET_INSPECTION_CAR_PHOTOS = 'carVerification/GET_INSPECTION_CAR_PHOTOS';
export const getInspectionCarPhotos = createAction(GET_INSPECTION_CAR_PHOTOS, api.getInspectionCarPhotos);

// [V2] 통합 리포트 qs 조립
const buildFreeReportQs = ({ homeServiceH3ApplicationId, meetgoReservationUuId, isGlobalAccess, employeeId, dealerId } = {}) => {
	if (homeServiceH3ApplicationId) return `?homeServiceH3ApplicationId=${homeServiceH3ApplicationId}`; // 믿고배송(홈서비스 H3)
	if (meetgoReservationUuId) return `?meetgoReservationUuId=${meetgoReservationUuId}`; // 믿고방문(예약)
	if (isGlobalAccess) return '?isGlobalAccess=true'; // 수출(해외 접근)
	if (employeeId) return `?employeeId=${employeeId}`; // 관리자(엘란)
	if (dealerId) return `?dealerId=${dealerId}`; // 딜러(광고 차량이 그 딜러 소유인지 서버가 검증)
	return '';
};
const buildPaidReportQs = ({ userId, employeeId } = {}) => {
	if (userId) return `?userId=${userId}`; // 유료 사용자
	if (employeeId) return `?employeeId=${employeeId}`; // 관리자(엘란)
	return '';
};

export const GET_CAR_REPORT = 'carVerification/GET_CAR_REPORT';
export const getUnifiedReportFreeAction = createAction(
	GET_CAR_REPORT,
	(params) => api.getUnifiedReportFree(params.carId, buildFreeReportQs(params)),
	(params) => params,
);

export const GET_CAR_REPORT_LITE = 'carVerification/GET_CAR_REPORT_LITE';
export const getUnifiedReportLiteAction = createAction(GET_CAR_REPORT_LITE, api.getUnifiedReportLite, (carId) => ({ carId }));

export const GET_CAR_REPORT_PAID = 'carVerification/GET_CAR_REPORT_PAID';
export const getUnifiedReportPaidAction = createAction(
	GET_CAR_REPORT_PAID,
	(params) => api.getUnifiedReportPaid(params.carId, buildPaidReportQs(params)),
	(params) => params,
);

export const GET_CAR_UNVERIFIED_REPORT = 'carVerification/GET_CAR_UNVERIFIED_REPORT';
export const getCarVerificationUnverifiedReportAction = createAction(
	GET_CAR_UNVERIFIED_REPORT,
	api.getCarVerificationUnverifiedReport,
	(params) => params,
);

const GET_SIMPLE_OPTION = 'carVerification/GET_SIMPLE_OPTION';
export const getSimpleOptionAction = createAction(GET_SIMPLE_OPTION, api.getReportSimpleOption, (params) => params);

const GET_SIMPLE_OPTION_V2 = 'carVerification/GET_SIMPLE_OPTION_V2';
export const getSimpleOptionV2Action = createAction(
	GET_SIMPLE_OPTION_V2,
	({ carId, optionIds }) => api.getSimpleReportV2(carId, optionIds?.length ? `?optionIds=${optionIds.join(',')}` : ''),
	(params) => params,
);

const GET_OPTION_STANDARD = 'cars/GET_OPTION_STANDARD';
export const getOptionStandard = createAction(GET_OPTION_STANDARD, api.getCarsOptionStandard);

const GET_REPORT_OPTION_DETAIL = 'cars/GET_OPTION_STANDARD';
export const getReportOptionDetail = createAction(GET_OPTION_STANDARD, api.getReportOptionDetail, (category) => ({ category }));

const GET_POSSIBLE_MEETGO_DELIVERY_CHECK = 'cars/GET_POSSIBLE_MEETGO_DELIVERY_CHECK';
export const getPossibleMeetgoDeliveryCheckAction = createAction(GET_POSSIBLE_MEETGO_DELIVERY_CHECK, api.getHomeServiceExistence);

const GET_POSSIBLE_MEETGO_VISIT_CHECK = 'cars/GET_POSSIBLE_MEETGO_VISIT_CHECK';
export const getPossibleMeetgoVisitCheckAction = createAction(GET_POSSIBLE_MEETGO_VISIT_CHECK, api.getEscrowV2ApplicationsConditions);

const SET_IS_DISABLE_REPORT = 'carVerification/SET_IS_DISABLE_REPORT';
export const setIsDisableReportAction = createAction(SET_IS_DISABLE_REPORT);

const initialState = {
	basic: {
		values: [],
	},
	interior: {
		values: [],
	},
	state: {
		values: [],
	},
	exterior: {
		values: [],
	},
	etc: {
		values: [],
	},
	diagnosis: {
		values: [],
	},
	car: null,
	carId: null,
	carPhotoObject: {
		photos: [],
	},
	verifier: null,
	carVerificationOption: {
		basic: [],
		state: [],
		exterior: [],
		interior: [],
		etc: [],
		diagnosis: [],
	},
	record: null,
	registration: null,
	updatedAt: undefined,
	verification: null,
	diagnosisUserInfo: null,
	diagnosisRegStatus: null,
	// fem 은 리포트 1.0 렌더러라 1.0 엔드포인트(`/verification/...`)를 부른다.
	// 2.0(검수 V4)은 car 가 `/v2/verification/...` 으로 따로 그린다.
	// **버전이 어긋나도 조회는 실패하지 않는다** — 디폴트 형태의 데이터가 200 으로 온다.
	// 그래서 2.0 차량은 여기 오기 전에 `withCarVerificationReportAuth` 가 막고 안내 후 화면을 닫는다.
	reportData: {},
	aiReport: null,
	reportFullData: {},
	optionStandard: {},
	optionDetail: {},
	simpleReport: null,
	simpleReportV2: null,
	possibleMeetgoDelivery: false,
	possibleMeetgoVisit: false,
	isDisableReport: false,
};

export default handleActions(
	{
		[SET_IS_DISABLE_REPORT]: (state, action) =>
			produce(state, (draft) => {
				draft.isDisableReport = action.payload;
				return draft;
			}),
		[SAVE_CAR_VERIFICATION_VALUES_IN_STORE]: (state, action) =>
			produce(state, (draft) => {
				const { category, values } = action.payload;
				draft[category].values = values;
				return draft;
			}),
		[CLEAR_CAR_VERIFICATION_VALUES]: (state) =>
			produce(state, (draft) => {
				Object.keys(carVerificationType.categoryType).forEach((key) => {
					const category = key.toLowerCase();

					draft[category].values = [];
				});

				return draft;
			}),
		...pender({
			type: SAVE_CAR_VERIFICATION_INFO,
			onError,
		}),
		...pender({
			type: ENROLL_CAR_VERIFICATION_INFO,
			onSuccess: (state) =>
				produce(state, (draft) => {
					draft.verification.completedVerification = true;

					return draft;
				}),
			onError,
		}),
		...pender({
			type: UPDATE_CAR_VERIFICATION_INFO,
			onError,
		}),
		...pender({
			type: GET_CAR_VERIFICATION_OPTION,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const options = action.payload.data;
					const category = action.meta.category.toLowerCase();
					if (options) {
						draft[category].values = options.map((item) => {
							if (item.dataType === carVerificationType.valueType.PICTURE) {
								return {
									optionId: item.id,
									option: item,
									value: [],
								};
							}

							return {
								optionId: item.id,
								option: item,
								value: item.defaultValue,
							};
						});

						draft.carVerificationOption[category] = options;
					}

					return draft;
				}),
		}),
		...pender({
			type: GET_CAR_VERIFICATION_INFO,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const values = action.payload.data;
					const category = action.meta.category.toLowerCase();

					draft.car = values.car || draft.car;
					draft.carId = values.carId || draft.carId;
					draft.verifier = values.verifier || draft.verifier;
					draft.updatedAt = values.updatedAt || draft.updatedAt;
					const { valuesMap, faultOptionMap, faultStatusMap } = valueMaps(values[category], values.itemPictures);

					const percentTreadIds = Object.values(carVerificationType.category.state).map((tread) => tread.항목.트레드_잔량[0]);
					const millimeterTreadIds = Object.values(carVerificationType.category.state).map((tread) => tread.항목.트레드_잔량[1]);
					const treadOptionIds = draft.carVerificationOption[category]
						.filter((item) => item.id && valuesMap.get(item.id))
						.map((item) => item.id);
					const treadRequiredIds = percentTreadIds.some((tread) => treadOptionIds.includes(tread)) ? percentTreadIds : millimeterTreadIds;
					draft[category] = {
						values: draft.carVerificationOption[category].map((item) => {
							const returnData = {
								optionId: item.id,
								option: item,
								value: valuesMap.get(item.id) || item.defaultValue,
								faultOptionValue: faultOptionMap.get(item.id) || null,
								faultStatusValue: faultStatusMap.get(item.id) || null,
								faultOption: item.faultOption,
							};
							if (item.dataType === carVerificationType.valueType.PICTURE) {
								return { ...returnData, value: valuesMap.get(item.id) || [] };
							}
							if (treadRequiredIds.includes(item.id)) {
								return {
									...returnData,
									option: {
										...item,
										required: treadRequiredIds.includes(item.id),
									},
								};
							}
							return returnData;
						}),
					};

					return draft;
				}),
		}),
		...pender({
			type: GET_DIAGNOSIS_INFO,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const [values, options] = action.payload.map((item) => item.data);

					const { valuesMap, faultOptionMap, faultStatusMap } = valueMaps(values.itemDiagnosis, values.itemPictures);
					const dignosisValues = values.diagnosis
						.map((item) => ({
							optionId: item.optionCd,
							option: {
								...item,
								id: item.optionCd,
								subCategory: carVerificationType.diagnosisSubCategory[item.subCategory],
								dataType: 'SELECT',
								defaultValue: 'PASS',
								expectedValue: 'PASS,CHECK',
							},
							originOption: item,
							value: item.value || 'PASS',
							faultOptionValue: item.faultOptionValue,
							faultStatusValue: item.faultStatusValue,
						}))
						.concat(
							options.map((item) => {
								if (item.dataType === carVerificationType.valueType.PICTURE) {
									return {
										optionId: item.id,
										option: item,
										value: valuesMap.get(item.id) || [],
										faultOptionValue: faultOptionMap.get(item.id) || null,
										faultStatusValue: faultStatusMap.get(item.id) || null,
									};
								}

								return {
									optionId: item.id,
									option: item,
									value: valuesMap.get(item.id) || item.defaultValue,
									faultOptionValue: faultOptionMap.get(item.id) || null,
									faultStatusValue: faultStatusMap.get(item.id) || null,
								};
							}),
						);

					Object.assign(draft, {
						diagnosis: {
							values: dignosisValues,
						},
						carVerificationOption: {
							...draft.carVerificationOption,
							diagnosis: dignosisValues.map((item) => item.option),
						},
					});

					return draft;
				}),
		}),
		...pender({
			type: GET_CAR_VERIFICATION_CONDITIONS,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;

					draft.verification = {
						...draft.verification,
						...data,
					};

					return draft;
				}),
		}),
		[SET_CAR_VERIFICATION_USER_TYPE]: (state, action) =>
			produce(state, (draft) => {
				const value = action.payload;

				draft.verification = {
					...draft.verification,
					userType: value.userType,
					carId: value.carId,
				};

				return draft;
			}),
		...pender({
			type: GET_DIAGNOSIS_USER_INFO,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;

					draft.diagnosisUserInfo = data;

					return draft;
				}),
		}),
		...pender({
			type: GET_DIAGNOSIS_REG_STATUS,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;
					const status = data?.filter((item) => item.step === 'VEHICLE_VERIFICATION')[0].status;

					draft.diagnosisRegStatus = status;

					return draft;
				}),
		}),
		...pender({
			type: GET_INSPECTION_CAR_PHOTOS,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					draft.carPhotoObject = action.payload.data;
					return draft;
				}),
		}),
		...pender({
			type: GET_CAR_REPORT,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { aiReport, verificationReport } = action.payload.data;
					draft.reportData = verificationReport ?? {};
					draft.aiReport = aiReport ?? null;
					draft.isDisableReport = verificationReport?.disabled ?? false;
					return draft;
				}),
		}),
		...pender({
			type: GET_CAR_REPORT_LITE,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { aiReport, verificationReport } = action.payload.data;
					draft.reportData = verificationReport ?? {};
					draft.aiReport = aiReport ?? null;
					draft.isDisableReport = verificationReport?.disabled ?? false;
					return draft;
				}),
		}),
		...pender({
			type: GET_CAR_UNVERIFIED_REPORT,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { aiReport, verificationReport } = action.payload.data;
					draft.reportData = verificationReport ?? {};
					draft.aiReport = aiReport ?? null;
					draft.isDisableReport = verificationReport?.disabled ?? false;
					return draft;
				}),
		}),
		...pender({
			type: GET_SIMPLE_OPTION,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					draft.simpleReport = action.payload.data;
					return draft;
				}),
		}),
		...pender({
			type: GET_SIMPLE_OPTION_V2,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					draft.simpleReportV2 = action.payload.data;
					return draft;
				}),
		}),
		...pender({
			type: GET_OPTION_STANDARD,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;
					draft.optionStandard = data || {};
				}),
		}),
		...pender({
			type: GET_REPORT_OPTION_DETAIL,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;
					const { category } = action.meta || {};
					set(draft, ['optionDetail', category], data);
				}),
		}),
		...pender({
			type: GET_POSSIBLE_MEETGO_DELIVERY_CHECK,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;
					draft.possibleMeetgoDelivery = data.existence;
				}),
		}),
		...pender({
			type: GET_POSSIBLE_MEETGO_VISIT_CHECK,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { data } = action.payload;
					draft.possibleMeetgoVisit = data.apply;
				}),
		}),
		...pender({
			type: GET_CAR_REPORT_PAID,
			onSuccess: (state, action) =>
				produce(state, (draft) => {
					const { aiReport, verificationReport } = action.payload.data;

					draft.reportData = verificationReport ?? {};
					draft.aiReport = aiReport ?? null;
					draft.isDisableReport = verificationReport?.disabled ?? false;

					return draft;
				}),
		}),
	},
	initialState,
);
